"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const timelineSteps = [{
  id: "consultation",
  title: "The Consultation",
  description: "Your journey begins with a bespoke skin analysis and aesthetic consultation to tailor a treatment plan unique to your skin's biology.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/beauty_consultation.png"
}, {
  id: "ritual",
  title: "The Ritual",
  description: "Experience our signature high-end aesthetics or beauty rituals, delivered with clinical precision and a gentle, restorative touch.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/beauty_ritual.png"
}, {
  id: "aftercare",
  title: "The Aftercare",
  description: "We ensure lasting radiance with a personalized home-care regimen and follow-up support to maintain your post-treatment glow.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/beauty_aftercare.png"
}, {
  id: "radiance",
  title: "The Radiance",
  description: "Step back into the world feeling renewed and confident, radiating the natural beauty nurtured at our Brooke sanctuary.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/beauty_radiance.png"
}];
function TimelineItem({
  step,
  index
}: {
  step: typeof timelineSteps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5,
    once: false
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="88880" ref={ref} className="relative flex flex-col md:flex-row items-center py-16 md:py-32 group">
      {}
      <div className={`absolute left-6 md:left-1/2 top-1/2 -translate-y-1/2 md:-translate-x-1/2 z-10 w-4 h-4 rounded-full transition-all duration-700 ease-out
          ${isVisible ? "bg-ring shadow-[0_0_30px_4px_var(--color-ring)] scale-125" : "bg-background border-2 border-border scale-100"}
        `} />

      {}
      <div className={`w-full md:w-1/2 flex flex-col gap-4 pl-20 md:pl-0 transition-all duration-700 ease-out
          ${isEven ? "md:pr-24 md:text-right md:items-end" : "md:pl-24 md:order-last md:items-start"}
          ${isVisible ? "opacity-100 translate-y-0" : "opacity-30 translate-y-8"}
        `}>
        <span className="text-ring font-serif text-xl italic tracking-widest">
          Phase 0{index + 1}
        </span>
        <h3 className="text-3xl md:text-4xl lg:text-5xl font-serif text-foreground tracking-wider">
          {step.title}
        </h3>
        <p className="text-muted-foreground font-sans leading-relaxed max-w-md text-lg">
          {step.description}
        </p>
      </div>

      {}
      <div className={`w-full md:w-1/2 pl-20 md:pl-0 mt-8 md:mt-0 transition-all duration-1000 ease-out delay-100
          ${isEven ? "md:pl-24" : "md:pr-24 md:order-first"}
          ${isVisible ? "opacity-100 scale-100" : "opacity-30 scale-95"}
        `}>
        <div className={`relative overflow-hidden aspect-[4/5] md:aspect-square w-full max-w-md mx-auto md:mx-0 transition-all duration-700
            ${isVisible ? "ring-1 ring-ring/20 shadow-lg shadow-ring/5" : "ring-1 ring-border/50 shadow-none"}
          `}>
          <img src={step.image} alt={step.title} className={`w-full h-full object-cover transition-transform duration-1000 ${isVisible ? "scale-100" : "scale-110"}`} />
        </div>
      </div>
    </div>;
}
export default function TheDesignTimeline() {
  return <section id="your-journey-to-radiance" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6">
        {}
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-foreground mb-6 tracking-wider">
            Your Journey to Radiance
          </h2>
          <p className="text-muted-foreground font-sans text-lg md:text-xl leading-relaxed">
            Experience a seamless path to aesthetic excellence at our Brooke sanctuary, where clinical expertise meets the serenity of a luxury spa.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-1/2 z-0" />

          {}
          <div className="relative z-10">
            {timelineSteps.map((step, i) => <div key={step.id} data-index={i} className="contents">
                <TimelineItem step={step} index={i} />
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-24 md:mt-32 text-center">
          <Button asChild size="lg" className="rounded-full px-10 py-6 text-lg font-serif tracking-widest bg-primary text-primary-foreground hover:bg-accent transition-colors duration-500">
            <Link to="/TheClinicPage">Begin Your Journey</Link>
          </Button>
        </div>
      </div>
    </section>;
}